"use client"

import { useMemo } from "react"
import { AlertCircle } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { Card } from "@/components/ui/card"
import { InsightsLockedNotice } from "@/components/shopify/shopify-insights-gate"
import { useShopifyRevenueSummaryQuery } from "@/hooks/queries/shopify-revenue-summary"
import type { ShopifyInsightsMode } from "@/hooks/use-shopify-insights-access"
import { computeDelta } from "@/lib/analytics/deltas"
import { getPreviousPeriodRange } from "./get-comparison-range"
import type { ShopifyPeriod } from "./period-constants"
import { PeriodField, StoreSelect } from "./report-scope-controls"
import { RevenueOrdersKpis } from "./revenue-orders-kpis"
import type { AttributionDateRange } from "./types"
import { buildOrdersPageHref } from "./utils"
import { useTranslations } from "next-intl"


interface RevenueSummaryViewProps {
  integrationId: string
  stores: { id: string; shop_domain: string }[]
  onStoreChange: (storeId: string) => void
  dateRange: AttributionDateRange
  period: ShopifyPeriod
  customRange: { from: string; to: string } | null
  onPeriodChange: (period: ShopifyPeriod, customRange?: { from: string; to: string }) => void
  minimumOrderTotal: number
  lockedMode: ShopifyInsightsMode
}

/** The dashboard without Insights: revenue and orders against the previous period, the rest locked. */
export function RevenueSummaryView({
  integrationId,
  stores,
  onStoreChange,
  dateRange,
  period,
  customRange,
  onPeriodChange,
  minimumOrderTotal,
  lockedMode,
}: RevenueSummaryViewProps) {
  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardRevenueSummaryView")

  const previousRange = useMemo(() => getPreviousPeriodRange(dateRange), [dateRange])
  const { data, isLoading, isError } = useShopifyRevenueSummaryQuery({
    integrationId,
    period: toPeriod(dateRange),
    previousPeriod: toPeriod(previousRange),
    timezone: dateRange.timezone,
    minimumOrderTotal,
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-background px-4 pb-5 pt-3">
        <StoreSelect stores={stores} selectedStore={integrationId} onStoreChange={onStoreChange} />
        <div className="hidden sm:block h-6 w-px bg-border" />
        <PeriodField period={period} customRange={customRange} onPeriodChange={onPeriodChange} dateRange={dateRange} />
      </div>

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription><span key={tUi("unableToLoadRevenueAndOrdersPleaseTry")}>{tUi("unableToLoadRevenueAndOrdersPleaseTry")}</span></AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {isLoading || !data ? (
            <>
              <KpiCardSkeleton />
              <KpiCardSkeleton />
            </>
          ) : (
            <RevenueOrdersKpis
              revenue={data.revenue}
              orders={data.orders}
              revenueDelta={data.previous && computeDelta(data.revenue, data.previous.revenue)}
              ordersDelta={data.previous && computeDelta(data.orders, data.previous.orders)}
              dailySeries={data.daily}
              ordersHref={buildOrdersPageHref(integrationId, dateRange)}
            />
          )}
        </div>
      )}

      <Card>
        <InsightsLockedNotice mode={lockedMode} />
      </Card>
    </div>
  )
}

function toPeriod(range: AttributionDateRange) {
  return { start: range.month_start_utc.toISOString(), end: range.month_end_utc.toISOString() }
}
